/**
 * F16-F18 design polish wave — `PolicyTier` (Wave 1.5, web-only per Decision 5).
 *
 * 3-column row visualising one cancellation-policy refund tier:
 *   threshold (≤Xh before) | refund % | progress bar
 *
 * Variants:
 * - Read-only (default): renders threshold + refund % as static mono text;
 *   refund % uses tier-tone colour (danger <50, warning 50-79, success ≥80).
 * - Editable (`readOnly={false}`): renders both values via `NumberInput`;
 *   `onChange` is required and fires with the FULL next state on either edit.
 * - Active-tier highlight (`active={true}`): wraps the row with a 2px accent
 *   border + soft accent background — used in `CancelTierTrack` consumers to
 *   highlight the user's current refund tier.
 *
 * Used by three callers:
 * - F12 cancellation tab (editable, 3 instances stacked)
 * - Web `CancelBookingDialog` (read-only with active-tier highlight)
 * - Admin `/admin/cancellation-policy` (read-only, no active tier)
 *
 * The component is "controlled" — the parent owns the value + drives saves.
 * `PolicyTier` itself never persists; orchestration lives in
 * `CancellationPolicyEditor` (portal F12 tab) or the admin read-only page.
 */
import { NumberInput } from '../NumberInput/NumberInput';
import { cn } from '../../utils/cn';

export interface PolicyTierProps {
  readonly hoursBefore: number;
  /** Refund percent, 0-100. Visually clamped to that range on the bar. */
  readonly refundPercent: number;
  /** Default false — renders editable NumberInput controls when false. */
  readonly readOnly?: boolean;
  /** Default false — when true, applies the active-tier accent highlight. */
  readonly active?: boolean;
  /**
   * Required when `readOnly` is false. Fires with the FULL next state on
   * either field change (mirrors the React controlled-input convention).
   */
  readonly onChange?: (next: { hoursBefore: number; refundPercent: number }) => void;
  /** Optional tier-name override; defaults to auto-generated threshold copy. */
  readonly label?: string;
  readonly className?: string;
}

/** Tier tone semantic resolver — locked: danger <50, warning 50-79, success ≥80. */
function tierTone(pct: number): 'danger' | 'warning' | 'success' {
  if (pct >= 80) return 'success';
  if (pct >= 50) return 'warning';
  return 'danger';
}

const TONE_TEXT = {
  danger: 'text-danger',
  warning: 'text-warning',
  success: 'text-success',
} as const;

const TONE_BAR = {
  danger: 'bg-danger',
  warning: 'bg-warning',
  success: 'bg-success',
} as const;

export function PolicyTier({
  hoursBefore,
  refundPercent,
  readOnly = false,
  active = false,
  onChange,
  label,
  className,
}: PolicyTierProps) {
  const tone = tierTone(refundPercent);
  const barPct = Math.min(100, Math.max(0, refundPercent));
  const thresholdLabel = label ?? `≤${hoursBefore}h before`;
  const refundLabel = `${refundPercent}%`;

  const handleHoursChange = (next: number) => {
    onChange?.({ hoursBefore: next, refundPercent });
  };
  const handlePercentChange = (next: number) => {
    onChange?.({ hoursBefore, refundPercent: next });
  };

  return (
    <div
      data-testid="policy-tier"
      data-active={active || undefined}
      data-tone={tone}
      className={cn(
        'grid grid-cols-[1fr_1fr_100px] gap-3 items-center rounded-xl border bg-surface p-3.5',
        active ? 'border-2 border-accent bg-accent/8' : 'border border-hairline-2',
        className,
      )}
    >
      {/* THRESHOLD column */}
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[9.5px] uppercase tracking-wider text-ink-3">
          Threshold
        </span>
        {readOnly ? (
          <span
            className="font-mono tabular-nums text-[15px] font-bold text-ink"
            data-testid="policy-tier-threshold-text"
          >
            {thresholdLabel}
          </span>
        ) : (
          <NumberInput
            value={hoursBefore}
            onChange={handleHoursChange}
            min={0}
            max={8760}
            size="sm"
            aria-label="Hours before slot"
            data-testid="policy-tier-threshold-input"
          />
        )}
      </div>

      {/* REFUND % column */}
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[9.5px] uppercase tracking-wider text-ink-3">Refund %</span>
        {readOnly ? (
          <span
            className={cn('font-mono tabular-nums text-[15px] font-bold', TONE_TEXT[tone])}
            data-testid="policy-tier-percent-text"
          >
            {refundLabel}
          </span>
        ) : (
          <NumberInput
            value={refundPercent}
            onChange={handlePercentChange}
            min={0}
            max={100}
            size="sm"
            aria-label="Refund percent"
            data-testid="policy-tier-percent-input"
          />
        )}
      </div>

      {/* PROGRESS BAR column */}
      <div
        role="progressbar"
        aria-label="Refund percent indicator"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={barPct}
        data-testid="policy-tier-bar"
        className="relative h-3 w-full overflow-hidden rounded-full bg-surface-2"
      >
        <div
          data-testid="policy-tier-bar-fill"
          className={cn(
            'absolute inset-y-0 start-0 transition-[width] duration-[200ms] ease-[cubic-bezier(.4,0,.2,1)]',
            TONE_BAR[tone],
          )}
          style={{ width: `${barPct}%` }}
        />
      </div>
    </div>
  );
}
